import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../models/user';
import { MfaEnrollmentWizardComponent } from '../components/mfa-enrollment-wizard/mfa-enrollment-wizard.component';

export interface MfaEnrollmentResult {
	enrolled?: boolean;
	skipped?: boolean;
	error?: boolean;
	mfaDisabled?: boolean;
	message?: string;
	method?: string;
	/**
	 * The verified email address, present when an Email OTP method was enrolled. Lets callers refresh the
	 * cached user's email + verified flag without waiting for the next login to re-fetch the profile.
	 */
	email?: string;
	/**
	 * The user left the login-time wizard via Sign out. Distinct from `skipped`: a skip defers enrollment
	 * and keeps the session, whereas this ends it. Callers must tear the session down and stop — once the
	 * grace period expires the wizard is non-skippable, and without this there is no way out of it at all.
	 */
	signedOut?: boolean;
}

@Injectable()
export class UserService {
	public user = new User();
	public user$: BehaviorSubject<any>;

	constructor(
		private _http: HttpClient,
		private _dialog: MatDialog
	) {
		this.user$ = new BehaviorSubject(this.user);
	}

	/**
	 * @api {put} /v2/Authentication/InetUser/Password Change Password
	 * @apiName changePassword
	 * @apiGroup Authentication
	 * @apiVersion 20.0.0
	 */
	changePassword(data) {
		return this._http.put<any>(`${environment.apiUrl}/v2/Authentication/InetUser/Password`, data);
	}

	deleteUser() {
		this.user = new User();
		localStorage.removeItem('Rapidship:user');
		this.user$.next(this.user);
	}

	/**
	 * @api {get} /v2/InetUsers/MiscData Get Inet Users Miscellaneous Data
	 * @apiName getInetUserMiscData
	 * @apiGroup InetUsers
	 * @apiVersion 20.0.0
	 */
	getInetUserMiscData() {
		return this._http.get<any>(`${environment.apiUrl}/v2/InetUsers/MiscData`);
	}

	getUser() {
		const user = JSON.parse(localStorage.getItem('Rapidship:user'));
		if (user && (!this.user || (this.user && !this.user.token))) {
			this.user$.next(user);
		}
		this.user = user;
		return this.user;
	}

	/**
	 * @api {post} /v2/Authentication/Reset Change Password
	 * @apiName reset
	 * @apiGroup InetUsers
	 * @apiVersion 20.1.0
	 */
	reset(data) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/Reset`, data);
	}

	/**
	 * @api {post} /v2/Authentication/ResetPassword Request Reset Password Link Via Email
	 * @apiName resetPassword
	 * @apiGroup Authentication
	 * @apiVersion 20.1.0
	 */
	resetPassword(emailAddress) {
		return this._http.post<any>(`${environment.apiUrl}/v2/Authentication/ResetPassword`, {emailAddress, userType: 'Inet'});
	}

	/**
	 * @api {put} /v2/InetUsers/MiscData Update Inet Users Miscellaneous Data
	 * @apiName setInetUserMiscData
	 * @apiGroup InetUsers
	 * @apiVersion 20.0.0
	 */
	setInetUserMiscData(data) {
		return this._http.put<any>(`${environment.apiUrl}/v2/InetUsers/MiscData`, data);
	}

	updateUser(user) {
		let userUpdate;
		const storedUser = JSON.parse(localStorage.getItem('Rapidship:user'));
		if (storedUser) {
			userUpdate = Object.assign(storedUser, user);
		} else {
			userUpdate = user;
		}
		localStorage.setItem('Rapidship:user', JSON.stringify(userUpdate));
		this.user = userUpdate;
		this.user$.next(this.user);
	}

	/**
	 * Reflect a now-verified email in the cached user after an Email OTP enrollment via Security "Add Email
	 * OTP". The Security tab binds to user.inetUser.email / user.emailVerified, so without this it keeps
	 * showing "unverified" (and, for a changed address, the old value) until the next login re-fetches the
	 * profile. The login-wizard path (openMfaEnrollmentWizard) applies the same two-line fold INLINE rather
	 * than calling this method - to keep it in one updateUser() call and avoid a double user$ emit - so keep
	 * the two in sync if this logic changes.
	 */
	markEmailVerified(result: MfaEnrollmentResult) {
		if (result?.method !== 'EmailOtp') { return; }
		const user = this.getUser();
		if (!user) { return; }
		if (result.email && user.inetUser) { user.inetUser.email = result.email; }
		user.emailVerified = true;
		this.updateUser(user);
	}

	/**
	 * @api {patch} /v2/InetUsers Update User Profile
	 * @apiName updateUserProfile
	 * @apiGroup InetUsers
	 * @apiVersion 20.0.0
	 */
	updateUserProfile(data) {
		return this._http.patch<any>(`${environment.apiUrl}/v2/InetUsers`, data);
	}

	openMfaEnrollmentWizard(): Observable<MfaEnrollmentResult | undefined> {
		const dialogConfig: MatDialogConfig = {
			disableClose: true,
			width: '500px',
			maxWidth: '90vw'
		};
		const currentUser = this.getUser();
		dialogConfig.data = {
			contactAdminPhone: currentUser?.mfaContactAdminPhone,
			contactAdminEmail: currentUser?.mfaContactAdminEmail
		};
		const wizardRef = this._dialog.open(MfaEnrollmentWizardComponent, dialogConfig);
		return new Observable<MfaEnrollmentResult | undefined>((observer) => {
			wizardRef.afterClosed().subscribe((result: MfaEnrollmentResult | undefined) => {
				const user = this.getUser();
				// F-139: clear the enrollment gate ONLY on a genuine resolution — the user enrolled a method or
				// the server recorded a skip. The old condition (`!signedOut`) also cleared it for `{error:true}`
				// (Start/Setup failed) and `undefined` (force-close), which fails the gate OPEN: the flag persists
				// to localStorage as resolved, the guard admits the route, and the user is never re-prompted even
				// though no method was enrolled and no skip was recorded. Gates must fail CLOSED — on error/undefined
				// leave the flag set so the wizard re-opens on the next attempt. Sign out is handled by the caller.
				if (user && (result?.enrolled || result?.skipped)) {
					user.mfaEnrollmentPending = false;
					if (result?.enrolled) {
						user.mfaEnrolledMethodCount = (user.mfaEnrolledMethodCount || 0) + 1;
						// A completed Email OTP enrollment has verified the address on the server. Fold it into
						// the SAME object so the single updateUser() below persists and emits user$ once — a
						// second updateUser here would double-emit (the extraneous-re-emit bug the F-151 note in
						// user-profile.component.ts guards against).
						if (result?.method === 'EmailOtp') {
							if (result.email && user.inetUser) { user.inetUser.email = result.email; }
							user.emailVerified = true;
						}
					} else if (result?.skipped) {
						user.mfaWizardSkipped = true;
					}
					this.updateUser(user);
				}
				observer.next(result);
				observer.complete();
			});
		});
	}
}
